/**
 * Las 7 áreas canónicas del sistema.
 *
 * FUENTE ÚNICA. No puede existir otra lista de áreas en el proyecto.
 *
 * Se usan en exactamente tres lugares (SPEC.md §2):
 *   1. `area` de cada elemento del Learning Bank  — NULLABLE
 *   2. Las autoevaluaciones al cerrar una sesión  — solo las relevantes
 *   3. El foco del Día 5
 *
 * La spec original definía 8 «tipos» de elemento y 7 «áreas» que no coincidían:
 * `Error`, `Useful expression` y `Work situation` son FORMAS de un elemento, no
 * áreas, y `lexical retrieval` —el ejemplo que la propia spec usa para ilustrar
 * el Día 5— no existía como tipo. El eje `kind` recoge la forma; este recoge
 * qué se entrena.
 */

export const AREAS = [
  'listening',
  'recuperacion_lexica',
  'habla_espontanea',
  'naturalidad',
  'gramatica_fluidez',
  'pronunciacion_prosodia',
  'interaccion_turnos',
] as const;

export type Area = (typeof AREAS)[number];

/** Nombre que ve el usuario. Interfaz en español (SPEC.md §18). */
export const NOMBRE_AREA: Record<Area, string> = {
  listening: 'Listening',
  recuperacion_lexica: 'Recuperación léxica',
  habla_espontanea: 'Habla espontánea',
  naturalidad: 'Naturalidad',
  gramatica_fluidez: 'Gramática y fluidez',
  pronunciacion_prosodia: 'Pronunciación y prosodia',
  interaccion_turnos: 'Interacción y turnos',
};

/** Qué cubre cada área. Se muestra al clasificar un elemento del banco. */
export const DESCRIPCION_AREA: Record<Area, string> = {
  listening: 'Comprensión de habla natural, velocidad, acentos, habla conectada.',
  recuperacion_lexica: 'Acceso rápido a la palabra o expresión que hace falta.',
  habla_espontanea: 'Producir sin guion, sin preparar la respuesta.',
  naturalidad: 'Word choice, colocaciones, sonar como un hablante y no como un manual.',
  gramatica_fluidez: 'Estructura correcta sin frenar el discurso.',
  pronunciacion_prosodia: 'Sonidos, stress, ritmo, entonación, reductions, linking.',
  interaccion_turnos: 'Turn-taking, repair, clarificación, reaccionar, entrar al turno.',
};

/**
 * `area` es NULLABLE en el Learning Bank (AJUSTE 1 aprobado).
 *
 * Un elemento sin clasificación natural se guarda sin área. La interfaz puede
 * SUGERIR una clasificación; nunca debe FORZAR una incorrecta. Solo los
 * elementos con área participan en la puntuación del Día 5.
 */
export type AreaOpcional = Area | null;

export function esArea(valor: unknown): valor is Area {
  return typeof valor === 'string' && (AREAS as readonly string[]).includes(valor);
}
